"use client";
import React, { useMemo, useState } from "react";
import MainInfo from "@/app/(pages)/Home/components/desktop/MainInfo";
import WeekForecastList from "@/app/(pages)/Home/components/desktop/WeekForecast/WeekForecastList";
import { WeatherPageProps } from "@/app/(pages)/Home/types";
import { useTranslation } from "react-i18next";
import { useSubscription } from "@/providers/WebSocketProvider";
import { CITY_LABEL } from "@/app/(pages)/Home/constants/shared";
import { buildWeatherPageModel } from "@/app/(pages)/Home/utils/buildWeatherPageModel";
import dynamic from "next/dynamic";

const DayHighchartsMetrics = dynamic(
  () => import("@/app/(pages)/Home/components/shared/Highcharts"),
  {
    ssr: false,
    loading: () => (
      <div className="loader top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
    ),
  },
);
const DayHighlight = dynamic(
  () => import("@/app/(pages)/Home/components/shared/DayHighlight"),
  {
    ssr: false,
  },
);

function WeatherPage({
  weather,
  lon,
  lat,
  city,
  isFirstEnter = false,
}: WeatherPageProps) {
  const [liveCity, setLiveCity] = useState(city);
  const { t, i18n } = useTranslation();

  useSubscription(CITY_LABEL, (v) => setLiveCity(v));

  const { jsonLd, currentDay } = useMemo(
    () =>
      buildWeatherPageModel({
        weather,
        city,
        lat,
        lon,
        t,
        language: i18n.language,
      }),
    [weather, city, lat, lon, t, i18n.language],
  );

  return (
    <div className="mx-auto flex h-fit w-full max-w-[1920px] flex-col items-center justify-center text-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <main className="flex w-full flex-col items-center gap-6 sm:items-start 2xl:gap-11">
        <div className="flex w-full flex-col gap-6 2xl:h-[26.5rem] 2xl:flex-row 2xl:gap-11">
          <section className="v-card relative order-2 flex min-w-0 flex-col overflow-hidden p-5 backdrop-blur-[2px] 2xl:order-1 2xl:h-full 2xl:min-w-[19rem] 2xl:shrink 2xl:basis-[30rem] 2xl:p-6">
            <WeekForecastList forecast={weather} currentDay={currentDay} />
          </section>
          <section className="v-card relative order-1 min-h-[21rem] min-w-0 flex-1 overflow-hidden p-5 2xl:order-2 2xl:h-full 2xl:min-h-0 2xl:p-6">
            <MainInfo
              forecast={weather}
              city={liveCity}
              currentDay={currentDay}
              isFirstEnter={isFirstEnter}
            />
          </section>
        </div>
        <div className="flex w-full flex-col gap-6 2xl:min-h-[24.125rem] 2xl:flex-row 2xl:items-stretch 2xl:gap-11">
          <section className="v-card relative flex min-w-0 flex-1 flex-col overflow-hidden p-5 backdrop-blur-[2px] 2xl:p-6">
            <DayHighchartsMetrics
              forecast={weather}
              city={liveCity}
              currentDay={currentDay}
              isMobile={false}
            />
          </section>
          <section className="v-card relative flex min-w-0 flex-col overflow-hidden p-5 backdrop-blur-[2px] 2xl:min-w-[19rem] 2xl:shrink 2xl:basis-[30rem] 2xl:p-6">
            <DayHighlight
              forecast={weather}
              currentDay={currentDay}
              variant="narrow"
            />
          </section>
        </div>
      </main>
    </div>
  );
}

export default WeatherPage;
